import Add from "@mui/icons-material/Add";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import { type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid-pro";
import { type MutableRefObject, useMemo, useRef } from "react";

import type { VariableDataType } from "@/components/rule-canvas-builder/hooks/useRuleVariables";

import CoAppDataGridDateTimeCell from "@/components/global/components/datagrid/CoAppDataGridDateTimeCell";
import CollectionEntryValueInput from "../CollectionEntryValueInput";
import {
	entryIdsEqual,
	entryValueIsEmpty,
	type CollectionEntry,
	type CollectionEntryId,
} from "../collectionEntryUtils";

const ROW_ACTIONS_WIDTH = 88;
const DATE_COLUMN_WIDTH = 176;
const VALUE_CELL_SX = {
	width: "100%",
	py: 0.75,
	"& .MuiFormHelperText-root": {
		minHeight: 20,
		lineHeight: "20px",
		margin: 0,
	},
} as const;

export interface CollectionEntryColumnHandlers {
	dataType: VariableDataType;
	draftEntries: CollectionEntry[];
	duplicateIndices: Set<number>;
	insertRowBelow: (afterIndex: number) => void;
	deleteRow: (entryId: CollectionEntryId) => void;
	updateEntryValue: (entryId: CollectionEntryId, value: string) => void;
}

function buildStableEntryColumns(
	dataType: VariableDataType,
	draftEntriesRef: MutableRefObject<CollectionEntry[]>,
	duplicateIndicesRef: MutableRefObject<Set<number>>,
	insertRowBelowRef: MutableRefObject<(afterIndex: number) => void>,
	deleteRowRef: MutableRefObject<(entryId: CollectionEntryId) => void>,
	updateEntryValueRef: MutableRefObject<(entryId: CollectionEntryId, value: string) => void>,
): GridColDef<CollectionEntry>[] {
	return [
		{
			field: "rowActions",
			headerName: "",
			width: ROW_ACTIONS_WIDTH,
			minWidth: ROW_ACTIONS_WIDTH,
			maxWidth: ROW_ACTIONS_WIDTH,
			resizable: false,
			sortable: false,
			filterable: false,
			renderCell: (params: GridRenderCellParams<CollectionEntry>) => {
				const draftEntries = draftEntriesRef.current;
				const rowIndex = draftEntries.findIndex((e) => entryIdsEqual(e.id, params.row.id));
				return (
					<Stack direction="row" sx={{ height: "100%", alignItems: "center" }}>
						<Tooltip title="Add row below" arrow>
							<IconButton
								size="small"
								onClick={() => insertRowBelowRef.current(rowIndex)}
								sx={{ color: "rgba(13, 31, 48, 0.45)" }}
							>
								<Add sx={{ fontSize: 18 }} />
							</IconButton>
						</Tooltip>
						<Tooltip title="Delete row" arrow>
							<IconButton
								size="small"
								onClick={() => deleteRowRef.current(params.row.id)}
								sx={{ color: "rgba(13, 31, 48, 0.45)" }}
							>
								<DeleteOutlined sx={{ fontSize: 18 }} />
							</IconButton>
						</Tooltip>
					</Stack>
				);
			},
		},
		{
			field: "value",
			headerName: "Value",
			flex: 1,
			minWidth: 200,
			sortable: true,
			renderCell: (params: GridRenderCellParams<CollectionEntry>) => {
				const draftEntries = draftEntriesRef.current;
				const duplicateIndices = duplicateIndicesRef.current;
				const rowIndex = draftEntries.findIndex((e) => entryIdsEqual(e.id, params.row.id));
				const isDuplicate = duplicateIndices.has(rowIndex);
				const isEmpty = entryValueIsEmpty(params.row.value);
				let helperText: string | undefined;
				if (isDuplicate) helperText = "Duplicate value";
				else if (isEmpty) helperText = "Required";

				return (
					<Box sx={VALUE_CELL_SX}>
						<CollectionEntryValueInput
							dataType={dataType}
							value={params.row.value}
							onChange={(v) => updateEntryValueRef.current(params.row.id, v)}
							error={isDuplicate || isEmpty}
							helperText={helperText}
							reserveHelperSpace
						/>
					</Box>
				);
			},
		},
		{
			field: "createdAt",
			headerName: "Created At",
			width: DATE_COLUMN_WIDTH,
			minWidth: DATE_COLUMN_WIDTH,
			sortable: true,
			renderCell: (params: GridRenderCellParams<CollectionEntry, string>) => (
				<CoAppDataGridDateTimeCell value={params.value} />
			),
		},
		{
			field: "updatedAt",
			headerName: "Updated At",
			width: DATE_COLUMN_WIDTH,
			minWidth: DATE_COLUMN_WIDTH,
			sortable: true,
			renderCell: (params: GridRenderCellParams<CollectionEntry, string>) => (
				<CoAppDataGridDateTimeCell value={params.value} />
			),
		},
	];
}

/** Column defs stable across edits; handlers and validation read from refs. */
export function useCollectionEntryColumns(handlers: CollectionEntryColumnHandlers): GridColDef<CollectionEntry>[] {
	const draftEntriesRef = useRef(handlers.draftEntries);
	const duplicateIndicesRef = useRef(handlers.duplicateIndices);
	const insertRowBelowRef = useRef(handlers.insertRowBelow);
	const deleteRowRef = useRef(handlers.deleteRow);
	const updateEntryValueRef = useRef(handlers.updateEntryValue);

	draftEntriesRef.current = handlers.draftEntries;
	duplicateIndicesRef.current = handlers.duplicateIndices;
	insertRowBelowRef.current = handlers.insertRowBelow;
	deleteRowRef.current = handlers.deleteRow;
	updateEntryValueRef.current = handlers.updateEntryValue;

	return useMemo(
		() =>
			buildStableEntryColumns(
				handlers.dataType,
				draftEntriesRef,
				duplicateIndicesRef,
				insertRowBelowRef,
				deleteRowRef,
				updateEntryValueRef,
			),
		[handlers.dataType],
	);
}
